import type { Ownership } from "@/hooks/useOrgEnrichment";
import { httpUrl } from "@/lib/publicOrganizationProfile";
import {
  LISTING_SERVICE_TYPES,
  listingMarketProblem,
  listingSourceUrlOk,
  listingSummaryProblem,
  type ListingServiceType,
} from "../../supabase/functions/_shared/listingFacts";

/**
 * Listing public facts (founder decision 2026-10-22; migration
 * 20261023120000): the staff-approved facts a basic listing shows — an About
 * summary in Compaire's words with its source, service types, markets served
 * and the year founded. Everything else on a listing stays "Not shared yet".
 *
 * The validation rule lives in supabase/functions/_shared/listingFacts.ts so
 * the Console and the Edge Function share one definition; it is re-exported
 * here so browser code imports "@/lib/listingFacts".
 */
export * from "../../supabase/functions/_shared/listingFacts";

/** What a listed page renders. Null when nothing is approved. */
export interface ListingFacts {
  /** Compaire's summary, always with its source. */
  summary: { text: string; sourceUrl: string; sourceHost: string } | null;
  serviceTypes: ListingServiceType[];
  markets: string[];
  foundedYear: number | null;
}

/** `listing_facts` as get_public_organization_profile sends it for a basic listing. */
export interface PublicListingFactsPayload {
  summary: string | null;
  source_url: string | null;
  source_host: string | null;
  service_types: string[];
  markets: string[];
  founded_year: number | null;
  approved_at: string | null;
}


/**
 * The renderable facts from the public payload, or null. Defensive on every
 * field (an older or malformed payload renders nothing rather than something
 * wrong). It applies the same rules as the database's read filter
 * (private.jsonb_listing_facts): a summary renders only when it passes
 * listingSummaryProblem and its source passes listingSourceUrlOk; markets
 * only when they pass listingMarketProblem; service types only from the
 * closed list; years only as four-digit integers.
 */
export function listingFactsFromPublic(raw: unknown): ListingFacts | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const p = raw as Partial<PublicListingFactsPayload>;
  const text = typeof p.summary === "string" && listingSummaryProblem(p.summary) === null ? p.summary : null;
  const sourceUrl =
    typeof p.source_url === "string" && listingSourceUrlOk(p.source_url) ? httpUrl(p.source_url) : null;
  // The label is always the validated URL's own host, never a separate value
  // that could disagree with where the link goes.
  let sourceHost: string | null = null;
  if (sourceUrl) {
    try {
      sourceHost = new URL(sourceUrl).hostname.replace(/^www\./i, "") || null;
    } catch {
      sourceHost = null;
    }
  }
  const summary = text && sourceUrl && sourceHost ? { text, sourceUrl, sourceHost } : null;
  const serviceTypes = Array.isArray(p.service_types)
    ? LISTING_SERVICE_TYPES.filter((t) => (p.service_types as unknown[]).includes(t))
    : [];
  const markets = Array.isArray(p.markets)
    ? p.markets.filter((m): m is string => typeof m === "string" && listingMarketProblem(m) === null)
    : [];
  const foundedYear =
    typeof p.founded_year === "number" && Number.isInteger(p.founded_year) && p.founded_year >= 1800 && p.founded_year <= 9999
      ? p.founded_year
      : null;
  if (!summary && serviceTypes.length === 0 && markets.length === 0 && foundedYear === null) return null;
  return { summary, serviceTypes, markets, foundedYear };
}

/* ─────────────── Console: prefill from staged suggestions ─────────────── */

/**
 * Staged service tags (the owner-editor vocabulary) that name a service type
 * from the closed list. The rest ("Tech Forward", "Top Producing"...) are
 * claims, not service types, and are never prefilled.
 */
const SERVICE_TAG_TO_TYPE: Record<string, ListingServiceType> = {
  "Luxury Focus": "Luxury",
  Commercial: "Commercial",
  "Property Management": "Property Management",
};

export interface ListingFactsDraft {
  summary: string;
  sourceUrl: string;
  serviceTypes: ListingServiceType[];
  /** One place per line. */
  markets: string;
  foundedYear: string;
}

export const EMPTY_LISTING_FACTS_DRAFT: ListingFactsDraft = {
  summary: "",
  sourceUrl: "",
  serviceTypes: [],
  markets: "",
  foundedYear: "",
};

interface SuggestionLike {
  field_key: string;
  suggested_value: unknown;
  source_url: string;
  created_at?: string;
}

interface StoredLike {
  summary: string | null;
  sourceUrl: string | null;
  serviceTypes: string[];
  markets: string[];
  foundedYear: number | null;
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

/**
 * The form's starting values: the approved facts when there are any (staff
 * edit what is live), otherwise the staged suggestions — the description's
 * page as the source (the description itself is NOT prefilled: it is the
 * site's own words, shown read-only beside the form by listingFactsReference);
 * service tags mapped onto the closed list; markets; founded year.
 * Nothing here is accepted: approving copies values into the listing-facts
 * store, and every suggestion stays pending for the owner.
 */
export function listingFactsPrefill(suggestions: readonly SuggestionLike[], stored: StoredLike | null | undefined): ListingFactsDraft {
  if (stored) {
    return {
      summary: stored.summary ?? "",
      sourceUrl: stored.sourceUrl ?? "",
      serviceTypes: LISTING_SERVICE_TYPES.filter((t) => stored.serviceTypes.includes(t)),
      markets: stored.markets.join("\n"),
      foundedYear: stored.foundedYear ? String(stored.foundedYear) : "",
    };
  }
  // Newest suggestion per field first (the read action orders by created_at desc).
  const first = (key: string) => suggestions.find((s) => s.field_key === key);
  const description = first("description");
  const tags = strings(first("serviceTags")?.suggested_value);
  const year = first("foundedYear")?.suggested_value;
  const yearText = typeof year === "number" ? String(year) : typeof year === "string" ? year.trim() : "";
  return {
    summary: "",
    sourceUrl: typeof description?.suggested_value === "string" ? description.source_url : "",
    serviceTypes: LISTING_SERVICE_TYPES.filter((t) => tags.some((tag) => SERVICE_TAG_TO_TYPE[tag] === t)),
    markets: strings(first("marketsServed")?.suggested_value).join("\n"),
    foundedYear: /^\d{4}$/.test(yearText) ? yearText : "",
  };
}

/** The site's own description (newest staged one), for read-only reference while staff write the summary. */
export function listingFactsReference(suggestions: readonly SuggestionLike[]): { text: string; sourceUrl: string } | null {
  const description = suggestions.find((s) => s.field_key === "description");
  return typeof description?.suggested_value === "string" && description.suggested_value.trim()
    ? { text: description.suggested_value.trim(), sourceUrl: description.source_url }
    : null;
}

/** The request payload for a draft (normalized again by the function and the database). */
export function listingFactsPayload(d: ListingFactsDraft): Record<string, unknown> {
  const year = d.foundedYear.trim();
  return {
    summary: d.summary.trim() || null,
    source_url: d.summary.trim() ? d.sourceUrl.trim() || null : null,
    service_types: d.serviceTypes,
    markets: d.markets
      .split(/\n|;/)
      .map((m) => m.trim())
      .filter(Boolean),
    founded_year: year === "" ? null : /^\d+$/.test(year) ? Number(year) : year,
  };
}

/**
 * WHICH LISTING IS CURRENT. The server's rule (private.is_listed_organization,
 * as amended by 20261023130000): `listed_at` is set and the organization is
 * not an owned, published one. An approved claim keeps the listing, and its
 * facts, public until the owner first publishes, which clears `listed_at`.
 * So an owned-but-unpublished listing is still current: its facts show and
 * staff can still edit them (staff_set_org_listing_facts accepts it).
 */
export function isCurrentListing(
  listedAt: string | null | undefined,
  ownership: Ownership,
  isPublished: boolean | null | undefined,
): boolean {
  if (!listedAt) return false;
  return !(ownership.status === "owned" && isPublished === true);
}
